import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// Import Breadcrumb
import Breadcrumb from "../../components/Common/Breadcrumb";

// Import API services
import {
  fetchAdminById,
  updateAdmin,
  updateAdminPassword,
} from "../../services/apiServices";
import { getUser, getAuthToken, setAuthData } from "../../helpers/auth-helpers";
import { toast } from "react-toastify";

const UserProfile = () => {
  document.title = "Profile | mrmto";

  const [activeTab, setActiveTab] = useState("1");
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [passwordUpdateLoading, setPasswordUpdateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Toggle tabs
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user ID and token
        const currentUser = getUser();
        const token = getAuthToken();

        if (!currentUser || !currentUser._id) {
          console.error("User data missing:", currentUser);
          setError("User information not found. Please log in again.");
          return;
        }

        console.log("Current user from localStorage:", currentUser);

        // Fetch user details from API
        const response = await fetchAdminById(currentUser._id);
        if (response && response.status) {
          console.log("API response:", response);
          setUserData(response.data.admin);
        } else {
          console.error("Failed to load user profile:", response);
          setError("Failed to load user profile.");
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError(
          err.response?.data?.message ||
            "An error occurred while loading profile.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Profile update form validation
  const profileValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userData?.name || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      try {
        setUpdateLoading(true);
        setError(null);
        setSuccess(null);

        if (!userData || !userData._id) {
          setError("User ID not found");
          return;
        }

        // Update profile
        const response = await updateAdmin(userData._id, {
          name: values.name,
          email: userData.email, // Keep the email unchanged
          isActive: userData.isActive,
        });

        if (response && response.success) {
          setUserData(response.admin);
          setSuccess("Profile updated successfully!");

          // Update user in localStorage to reflect changes
          const user = getUser();
          if (user) {
            user.name = values.name;
            localStorage.setItem("user", JSON.stringify(user));
          }
        } else {
          setError("Failed to update profile");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to update profile");
        console.error("Error updating profile:", err);
      } finally {
        setUpdateLoading(false);
      }
    },
  });

  // Password change form validation
  const passwordValidation = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current password is required"),
      newPassword: Yup.string()
        .required("New password is required")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: Yup.string()
        .required("Please confirm your password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      try {
        setPasswordUpdateLoading(true);
        setError(null);
        setSuccess(null);

        if (!userData || !userData._id) {
          setError("User ID not found");
          return;
        }

        // Update password
        const response = await updateAdminPassword(userData._id, {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });

        if (response && response.success) {
          setSuccess("Password updated successfully!");
          // Reset password fields
          passwordValidation.resetForm();
        } else {
          setError("Failed to update password");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to update password");
        console.error("Error updating password:", err);
      } finally {
        setPasswordUpdateLoading(false);
      }
    },
  });

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading profile...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="mrmto" breadcrumbItem="My Profile" />

          {error && (
            <Alert color="danger" className="mb-4">
              <div>{error}</div>
            </Alert>
          )}

          {success && (
            <Alert color="success" className="mb-4">
              <div>{success}</div>
            </Alert>
          )}

          <Row>
            <Col lg="4">
              <Card>
                <CardBody>
                  <div className="text-center">
                    <div className="mb-3">
                      <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS7ZH4p0SyCKnRg8apVdk-DzSNW_lbUdWS9jA&s"
                        alt="Profile"
                        className="avatar-xl rounded-circle img-thumbnail"
                      />
                    </div>
                    <h5 className="font-size-16 mb-1">
                      {userData?.name || "User"}
                    </h5>
                    <p className="text-muted">
                      {userData?.email || "user@example.com"}
                    </p>
                    <div className="mt-3">
                      <p className="text-muted mb-1">
                        <strong>Status:</strong>{" "}
                        <span
                          className={`badge ${
                            userData?.isActive ? "bg-success" : "bg-danger"
                          }`}>
                          {userData?.isActive ? "Active" : "Inactive"}
                        </span>
                      </p>
                      <p className="text-muted mb-1">
                        <strong>ID:</strong> {userData?._id}
                      </p>
                      <p className="text-muted mb-0">
                        <strong>Created:</strong>{" "}
                        {userData?.createdAt
                          ? new Date(userData.createdAt).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg="8">
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom">
                    <NavItem>
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggleTab("1")}>
                        <span className="d-none d-sm-block">
                          Update Profile
                        </span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => toggleTab("2")}>
                        <span className="d-none d-sm-block">
                          Change Password
                        </span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="p-3 text-muted">
                    <TabPane tabId="1">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          profileValidation.handleSubmit();
                          return false;
                        }}>
                        <Row className="mb-3">
                          <Col md={12}>
                            <Label htmlFor="name">Name</Label>
                            <Input
                              type="text"
                              id="name"
                              name="name"
                              className="form-control"
                              placeholder="Enter your name"
                              onChange={profileValidation.handleChange}
                              onBlur={profileValidation.handleBlur}
                              value={profileValidation.values.name || ""}
                              invalid={
                                profileValidation.touched.name &&
                                profileValidation.errors.name
                                  ? true
                                  : false
                              }
                            />
                            {profileValidation.touched.name &&
                            profileValidation.errors.name ? (
                              <FormFeedback type="invalid">
                                {profileValidation.errors.name}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={12}>
                            <Label htmlFor="email">Email</Label>
                            <Input
                              type="email"
                              id="email"
                              name="email"
                              className="form-control"
                              value={userData?.email || ""}
                              disabled
                            />
                            <small className="form-text text-muted">
                              Email cannot be updated
                            </small>
                          </Col>
                        </Row>
                        <div className="text-center mt-4">
                          <Button
                            type="submit"
                            color="primary"
                            disabled={updateLoading}>
                            {updateLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"></span>
                                Updating...
                              </>
                            ) : (
                              "Update Profile"
                            )}
                          </Button>
                        </div>
                      </Form>
                    </TabPane>
                    <TabPane tabId="2">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          passwordValidation.handleSubmit();
                          return false;
                        }}>
                        <Row className="mb-3">
                          <Col md={12}>
                            <Label htmlFor="currentPassword">
                              Current Password
                            </Label>
                            <Input
                              type="password"
                              id="currentPassword"
                              name="currentPassword"
                              className="form-control"
                              placeholder="Enter current password"
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              value={passwordValidation.values.currentPassword}
                              invalid={
                                passwordValidation.touched.currentPassword &&
                                passwordValidation.errors.currentPassword
                                  ? true
                                  : false
                              }
                            />
                            {passwordValidation.touched.currentPassword &&
                            passwordValidation.errors.currentPassword ? (
                              <FormFeedback type="invalid">
                                {passwordValidation.errors.currentPassword}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={12}>
                            <Label htmlFor="newPassword">New Password</Label>
                            <Input
                              type="password"
                              id="newPassword"
                              name="newPassword"
                              className="form-control"
                              placeholder="Enter new password"
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              value={passwordValidation.values.newPassword}
                              invalid={
                                passwordValidation.touched.newPassword &&
                                passwordValidation.errors.newPassword
                                  ? true
                                  : false
                              }
                            />
                            {passwordValidation.touched.newPassword &&
                            passwordValidation.errors.newPassword ? (
                              <FormFeedback type="invalid">
                                {passwordValidation.errors.newPassword}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={12}>
                            <Label htmlFor="confirmPassword">
                              Confirm New Password
                            </Label>
                            <Input
                              type="password"
                              id="confirmPassword"
                              name="confirmPassword"
                              className="form-control"
                              placeholder="Confirm new password"
                              onChange={passwordValidation.handleChange}
                              onBlur={passwordValidation.handleBlur}
                              value={passwordValidation.values.confirmPassword}
                              invalid={
                                passwordValidation.touched.confirmPassword &&
                                passwordValidation.errors.confirmPassword
                                  ? true
                                  : false
                              }
                            />
                            {passwordValidation.touched.confirmPassword &&
                            passwordValidation.errors.confirmPassword ? (
                              <FormFeedback type="invalid">
                                {passwordValidation.errors.confirmPassword}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        </Row>
                        <div className="text-center mt-4">
                          <Button
                            type="submit"
                            color="primary"
                            disabled={passwordUpdateLoading}>
                            {passwordUpdateLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"></span>
                                Updating Password...
                              </>
                            ) : (
                              "Update Password"
                            )}
                          </Button>
                        </div>
                      </Form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
